import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  CellValueChangedEvent,
  ColDef,
  GetRowIdParams,
  GridOptions,
  GridApi,
  GridReadyEvent,
  RowModelType,
  StateUpdatedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { concat, forkJoin, Observable } from 'rxjs';
import { DropTrailerLadingModalComponent } from './drop-trailer-lading-modal/drop-trailer-lading-modal.component';
import {
  DropTrailerFlowDateDialogComponent,
  DropTrailerFlowDateDialogData,
  DropTrailerFlowDateDialogResult,
} from './drop-trailer-flow-date-dialog.component';
import {
  DropTrailerAppointmentWindowDialogComponent,
  DropTrailerAppointmentWindowDialogData,
  DropTrailerAppointmentWindowDialogResult,
} from './drop-trailer-appointment-window-dialog.component';
import {
  DropTrailerFlowManagerDialogComponent,
  DropTrailerFlowManagerDialogData,
  DropTrailerFlowManagerDialogResult,
  DropTrailerFlowManagerItem,
} from './drop-trailer-flow-manager-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog.component';
import { dropTrailerDefaultGridState } from '../default-grid-states/drop-trailer-default-grid-state';
import 'ag-grid-enterprise';
import { UserGridStateService } from '../user-grid-state.service';
import { AuthService, CurrentUser } from '../auth.service';
import { DropTrailer } from '../models/drop-trailer.model';
import { DropTrailerNotesModalComponent } from './drop-trailer-notes-modal/drop-trailer-notes-modal.component';
import {
  DropTrailerFlagConfig,
  DropTrailerFlagsCellRendererComponent,
} from './drop-trailer-flags-cell-renderer.component';
import {
  DropTrailerPartnerCarrierCellEditorComponent,
  DropTrailerPartnerCarrierEditValue,
} from './drop-trailer-partner-carrier-cell-editor.component';

interface DropTrailerInsight {
  key: string;
  title: string;
  value: number;
  description: string;
  facts: Array<{
    label: string;
    value: string;
  }>;
  tone: 'sky' | 'amber' | 'emerald' | 'rose';
  icon: string;
}

interface DropTrailerAnalyticsVolume {
  ytd_trailers?: number;
  ytd_cartons?: number;
  ytd_received_cartons?: number;
  ytd_audited_cartons?: number;
  ytd_received_not_audited_cartons?: number;
  ytd_product_barcodes?: number;
  ytd_planned_cartons?: number;
  mtd_trailers?: number;
  mtd_cartons?: number;
  mtd_received_cartons?: number;
  mtd_audited_cartons?: number;
  mtd_received_not_audited_cartons?: number;
  mtd_product_barcodes?: number;
  mtd_planned_cartons?: number;
  avg_cartons_per_trailer_ytd?: number;
  avg_cartons_per_trailer_mtd?: number;
}

interface DropTrailerLiveOps {
  active_trailers: number;
  at_dc: number;
  pool_work: number;
  scan_finished: number;
  waiting_load: number;
  ready_pickup: number;
  offloading: number;
  ready_scan: number;
  notify_due: number;
  admin_due: number;
  missing_trailer_number: number;
  active_product_barcodes: number;
  active_received_cartons: number;
  active_audited_cartons: number;
  active_received_not_audited_cartons: number;
}

interface DropTrailerAnalyticsLeader {
  name?: string;
  uuid?: string;
  trailers?: number;
  cartons?: number;
  audited_cartons?: number;
  planned_cartons?: number;
}

interface DropTrailerAnalyticsResponse {
  success?: boolean;
  as_of?: string;
  timezone?: string;
  volume?: DropTrailerAnalyticsVolume;
  live_ops?: Partial<DropTrailerLiveOps>;
  leaders?: {
    top_distribution_centers?: DropTrailerAnalyticsLeader[];
    top_carriers?: DropTrailerAnalyticsLeader[];
    top_pool_points?: DropTrailerAnalyticsLeader[];
  };
  monthly_trend?: Array<Record<string, any>>;
}

interface DropTrailerLadingStatusSync {
  status: string;
  selectedDate?: string;
  selectedTime?: string;
}

@Component({
  selector: 'app-drop-trailers',
  templateUrl: './drop-trailers.component.html',
  styleUrls: ['./drop-trailers.component.scss']
})
export class DropTrailersComponent implements OnInit {
  universalFilter = '';
  onFilterTextBoxChanged(): void {
    this.gridApi?.setGridOption('quickFilterText', this.universalFilter);
  }

  private gridApi!: GridApi<any>;
  rowModelType: RowModelType = 'clientSide';
  rowData: DropTrailer[] = [];
  loading = true;
  errorMessage = '';
  analyticsLoading = false;
  analyticsError = '';
  dropTrailerInsights: DropTrailerInsight[] = [];
  activeInsightKey = '';
  public gridOptions: GridOptions<DropTrailer> = {};
  public initialState = JSON.parse(JSON.stringify(dropTrailerDefaultGridState));
  public gridReady = false;
  private readonly frozenDropTrailerColumnIds = [
    'work_order_details',
    'alternate_reference',
    'booking_number',
    'carrier_name',
    'carrier_trailer',
    'drop_trailer_flow',
  ];
  private readonly requiredDropTrailerColumnIds = [
    'lading_notes',
  ];
  private readonly preferredDropTrailerColumnOrder = [
    'work_order_details',
    'alternate_reference',
    'booking_number',
    'carrier_name',
    'carrier_trailer',
    'drop_trailer_flow',
    'drop_trailer_admin_flags',
    'lading_notes',
    'shipper_name',
    'consignee_name',
    'consignee_locality',
    'lading_number',
    'pickup_reference_number',
    'load_pieces',
    'load_start',
    'appointment_date',
    'appointment_window',
    'current_drop_status',
    'interline_carrier_name',
    'lading_id',
  ];

  themeClass = 'ag-theme-ulaap';
  themeMode: 'dark' | 'light' = 'dark';
  private readonly themeStorageKey = 'drop-trailers-theme';
  carrierLimitedMode = false;
  private readonly carrierPrivilegedRoleNames = new Set(['ADMIN', 'SUPER_ADMIN', 'MASTER', 'DISPATCHER', 'OPERATIONS', 'CUSTOMER_CARE']);
  private readonly adminRoleNames = new Set(['ADMIN', 'SUPER_ADMIN', 'MASTER']);
  adminMode = false;

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  private apiUrl = '/api/drop_trailers/drop_trailers.php';
  private analyticsApiUrl = '/api/drop_trailers/drop_trailer_analytics.php';
  private readonly dropTrailerPageSize = 1000;
  private dropTrailerAnalytics: DropTrailerAnalyticsResponse | null = null;
  private revertingDropTrailerCell = false;
  private readonly editableDropTrailerFields = new Set([
    'booking_number',
    'carrier_trailer',
    'lading_number',
    'load_pieces',
    'load_start',
    'appointment_date',
    'appointment_start',
    'appointment_end',
    'pickup_reference_number',
    'interline_carrier_name',
    'interline_carrier_contact_uuid',
    'drop_trailer_status_note',
    'dropped_dc',
    'dropped_dc_at',
    'dropped_dc_date',
    'dropped_dc_time',
    'loaded_dc',
    'loaded_dc_at',
    'loaded_dc_date',
    'loaded_dc_time',
    'picked_up_dc',
    'picked_up_dc_at',
    'picked_up_dc_date',
    'picked_up_dc_time',
    'dropped_pool',
    'dropped_pool_at',
    'dropped_pool_date',
    'dropped_pool_time',
    'pool_offload_started',
    'pool_offload_started_at',
    'pool_offload_started_date',
    'pool_offload_started_time',
    'pool_offload_finished',
    'pool_offload_finished_at',
    'pool_offload_finished_date',
    'pool_offload_finished_time',
    'pool_scanning_started',
    'pool_scanning_started_at',
    'pool_scanning_started_date',
    'pool_scanning_started_time',
    'pool_scanning_finished',
    'pool_scanning_finished_at',
    'pool_scanning_finished_date',
    'pool_scanning_finished_time',
    'carrier_confirmation_sent',
    'pars_sent',
    'pars_received',
    'pars_to_customs',
    'appointment_plus_appointment_made',
    'appointment_plus_unload_time_set',
    'notification_trailer_empty',
  ]);
  private readonly carrierEditableBaseFields = new Set([
    'booking_number',
    'carrier_trailer',
    'interline_carrier_name',
    'interline_carrier_contact_uuid',
    'drop_trailer_status_note',
    'load_start',
    'appointment_date',
  ]);
  private readonly gridStateSourcesToPersist = new Set([
    'aggregation',
    'columnOrder',
    'columnPinning',
    'columnSizing',
    'columnVisibility',
    'filter',
    'pivot',
    'rowGroup',
    'sideBar',
    'sort',
  ]);
  private readonly workflowFlagConfigs: DropTrailerFlagConfig[] = [
    { field: 'dropped_dc', label: 'Dropped at DC', shortLabel: 'Drop', tone: 'sky', dateField: 'dropped_dc_date', timeField: 'dropped_dc_time' },
    { field: 'loaded_dc', label: 'Loaded at DC', shortLabel: 'Load', tone: 'amber', dateField: 'loaded_dc_date', timeField: 'loaded_dc_time' },
    { field: 'picked_up_dc', label: 'Picked up from DC', shortLabel: 'Pick', tone: 'emerald', dateField: 'picked_up_dc_date', timeField: 'picked_up_dc_time' },
    { field: 'dropped_pool', label: 'Dropped at pool', shortLabel: 'Pool', tone: 'violet', dateField: 'dropped_pool_date', timeField: 'dropped_pool_time' },
    { field: 'pool_offload_started', label: 'Pool offload started', shortLabel: 'OS', tone: 'amber', dateField: 'pool_offload_started_date', timeField: 'pool_offload_started_time' },
    { field: 'pool_offload_finished', label: 'Pool offload finished', shortLabel: 'OF', tone: 'emerald', dateField: 'pool_offload_finished_date', timeField: 'pool_offload_finished_time' },
    { field: 'pool_scanning_started', label: 'Pool scanning started', shortLabel: 'SS', tone: 'cyan', dateField: 'pool_scanning_started_date', timeField: 'pool_scanning_started_time' },
    { field: 'pool_scanning_finished', label: 'Pool scanning finished', shortLabel: 'SF', tone: 'emerald', dateField: 'pool_scanning_finished_date', timeField: 'pool_scanning_finished_time' },
  ];
  private readonly adminFlagConfigs: DropTrailerFlagConfig[] = [
    { field: 'carrier_confirmation_sent', label: 'Carrier Confirmation sent', shortLabel: 'CC Sent', tone: 'sky' },
    { field: 'pars_received', label: 'PARS received', shortLabel: 'PARS Rec', tone: 'cyan', valueType: 'timestamp' },
    { field: 'pars_to_customs', label: 'PARS sent to customs', shortLabel: 'PARS Sent', tone: 'sky', valueType: 'timestamp' },
    { field: 'appointment_plus_appointment_made', label: 'Open Dock appointment made', shortLabel: 'OD Appt', tone: 'violet' },
    { field: 'appointment_plus_unload_time_set', label: 'Open Dock completed', shortLabel: 'OD Comp', tone: 'amber' },
    { field: 'notification_trailer_empty', label: 'Open Dock trailer empty notification sent', shortLabel: 'Trl Empty', tone: 'emerald' },
  ];
  private readonly ladingStatusByFlowField: Record<string, string> = {
    loaded_dc: 'Loaded Shipper',
    picked_up_dc: 'En Route Consignee',
    dropped_pool: 'Delivered Consignee',
  };

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private router: Router,
    private gridStateService: UserGridStateService,
    private toastr: ToastrService,
    private authService: AuthService
    ) {}


  addDropTrailer(): void {
    const dialogRef: MatDialogRef<DropTrailerLadingModalComponent> = this.dialog.open(DropTrailerLadingModalComponent, {
      width: '96vw',
      maxWidth: '1120px',
      maxHeight: '92vh',
      panelClass: 'drop-trailer-lading-dialog',
      data: { themeMode: this.themeMode },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refreshGrid) {
        this.refreshData();
      }
    });
  }
  
  ngOnInit(): void {
    this.initializeTheme();
    this.loadAnalytics();
    this.initializeDropTrailerGrid();
  }

  private initializeDropTrailerGrid(): void {
    this.authService.ensureCurrentUserLoaded().subscribe({
      next: (currentUser) => {
        this.carrierLimitedMode = this.isCarrierLimitedUser(currentUser);
        this.adminMode = this.isAdminUser(currentUser);
        this.gridOptions = this.buildGridOptions();
        this.fetchAndApplyGridState();
      },
      error: (error) => {
        console.warn('Drop trailer auth context failed to load.', error);
        this.carrierLimitedMode = false;
        this.adminMode = false;
        this.gridOptions = this.buildGridOptions();
        this.fetchAndApplyGridState();
      },
    });
  }

  private buildGridOptions(): GridOptions<DropTrailer> {
    return {
      rowHeight: 46,
      headerHeight: 44,
      floatingFiltersHeight: 34,
      animateRows: true,
      pagination: true,
      paginationPageSize: 50,
      paginationPageSizeSelector: [25, 50, 100, 250],
      tooltipShowDelay: 350,
      enableCellTextSelection: true,
      enableCharts: true,
      cellSelection: { handle: { mode: 'range' } },
      rowSelection: {
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: false,
      },
      rowGroupPanelShow: 'onlyWhenGrouping',
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
      alwaysShowHorizontalScroll: true,
      getRowId: (params: GetRowIdParams<DropTrailer>) => String(
        params.data?.lading_uuid
        || params.data?.lading_id
        || params.data?.alternate_reference
        || params.data?.booking_number
        || (params.data as any)?.lading_number
        || params.data?.trip_number
        || ''
      ),
      isExternalFilterPresent: () => !!this.activeInsightKey,
      doesExternalFilterPass: (node) => this.matchesActiveInsight(node.data as DropTrailer),
      rowClassRules: {
        'dt-row--complete': (params) => this.isDropTrailerFlowComplete(params.data as DropTrailer),
        'dt-row--pool': (params) => this.isTrue(params.data?.dropped_pool) && !this.isTrue(params.data?.pool_offload_finished),
        'dt-row--at-dc': (params) => this.isTrue(params.data?.dropped_dc) && !this.isTrue(params.data?.picked_up_dc),
      },
      sideBar: {
        hiddenByDefault: true,
        toolPanels: ['columns', 'filters'],
      },
      postSortRows: (params) => this.moveCompletedDropTrailerRowsToBottom(params.nodes),
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent' },
          { statusPanel: 'agAggregationComponent' },
        ],
      },
      columnDefs: this.buildColumnDefs(),
      onCellValueChanged: (event) => this.onDropTrailerCellValueChanged(event),
      defaultColDef: {
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          caseSensitive: false,
          buttons: ['apply', 'clear', 'reset'],
        },
        resizable: true,
        enableRowGroup: true,
        enablePivot: true,
        minWidth: 118,
      },
      rowModelType: 'clientSide',
    };
  }

  private buildColumnDefs(): ColDef<DropTrailer>[] {
    const columns: ColDef<DropTrailer>[] = [];

    if (this.canOpenWorkOrderDetails()) {
      columns.push(this.detailsCol());
    }

    columns.push(
      this.textCol('FBI #', 'alternate_reference', {
        pinned: 'left',
        width: 138,
        minWidth: 124,
        cellClass: 'dt-strong-cell',
      }),
      this.editableTextCol('Carrier #', 'booking_number', {
        pinned: 'left',
        width: 140,
      }),
      this.textCol('Carrier', 'carrier_name', {
        pinned: 'left',
        width: 190,
        filter: 'agSetColumnFilter',
      }),
      this.textCol('Trailer #', 'carrier_trailer', {
        pinned: 'left',
        width: 132,
        minWidth: 120,
        editable: (params) => !!params.data?.lading_uuid && this.canEditDropTrailerField('carrier_trailer'),
        cellClass: ['dt-strong-cell', 'dt-editable-cell'],
      }),
      this.workflowFlagsCol(),
      ...(this.canAccessAdminFlags() ? [this.adminFlagsCol()] : []),
      this.ladingNotesCol(),
      this.textCol('DC (Shipper)', 'shipper_name', {
        width: 190,
        filter: 'agSetColumnFilter',
      }),
      this.textCol('Consignee', 'consignee_name', {
        width: 220,
        filter: 'agSetColumnFilter',
      }),
      this.textCol('Consignee City', 'consignee_locality', {
        width: 150,
        filter: 'agSetColumnFilter',
      }),
      this.masterBolCol(),
      this.editableTextCol('Pick Up #', 'pickup_reference_number', { width: 124 }),
      this.numberCol('Pieces', 'load_pieces', {
        width: 112,
        editable: (params: any) => !!params.data?.lading_uuid && this.canEditDropTrailerField('load_pieces'),
        valueParser: (params) => this.parseEditableNumber(params.newValue),
        cellClass: ['dt-number-cell', 'dt-editable-cell'],
      }),
      this.editableDateCol('Est PU Date', 'load_start'),
      this.editableDateCol('Est Del Date', 'appointment_date'),
      this.appointmentWindowCol(),
      this.statusCol(),
      this.partnerCarrierCol(),
      this.textCol('Drop Note', 'drop_trailer_status_note', {
        width: 280,
        minWidth: 220,
        flex: 1,
        hide: true,
        tooltipField: 'drop_trailer_status_note',
        editable: (params) => !!params.data?.lading_uuid && this.canEditDropTrailerField('drop_trailer_status_note'),
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellClass: ['dt-note-cell', 'dt-editable-cell'],
      }),
      this.textCol('LADING ID', 'lading_id', {
        width: 126,
        minWidth: 112,
        cellClass: 'dt-strong-cell',
      }),
      ...this.hiddenFlagColumns(this.canAccessAdminFlags() ? [...this.workflowFlagConfigs, ...this.adminFlagConfigs] : this.workflowFlagConfigs),
    );

    return columns;
  }


  private masterBolCol(): ColDef<DropTrailer> {
    return this.editableTextCol('Master BOL', 'lading_number', {
      width: 140,
      valueGetter: (params) => (params.data as any)?.lading_number || (params.data as any)?.trip_number || '',
      valueSetter: (params) => {
        const nextValue = typeof params.newValue === 'string' ? params.newValue.trim() : params.newValue;
        const currentValue = (params.data as any)?.lading_number || (params.data as any)?.trip_number || '';
        if (this.areGridValuesEqual(currentValue, nextValue)) {
          return false;
        }

        (params.data as any).lading_number = nextValue || null;
        return true;
      },
    });
  }

  private canOpenWorkOrderDetails(): boolean {
    return !this.carrierLimitedMode;
  }

  private isCarrierLimitedUser(currentUser: CurrentUser | null): boolean {
    const roles = this.normalizedRoleNames(currentUser);
    return roles.includes('CARRIER') && !roles.some((role) => this.carrierPrivilegedRoleNames.has(role));
  }

  private isAdminUser(currentUser: CurrentUser | null): boolean {
    return this.normalizedRoleNames(currentUser).some((role) => this.adminRoleNames.has(role));
  }

  private normalizedRoleNames(currentUser: CurrentUser | null): string[] {
    return (currentUser?.roles || []).map((role) => String(role || '').toUpperCase());
  }

  private canAccessAdminFlags(): boolean {
    return this.adminMode;
  }

  private canEditDropTrailerField(field: string): boolean {
    if (!field) return false;

    if (!this.canAccessAdminFlags() && this.isAdminFlagField(field)) {
      return false;
    }

    if (this.carrierLimitedMode) {
      return this.isCarrierAllowedDropTrailerField(field);
    }

    return this.editableDropTrailerFields.has(field);
  }

  private isCarrierAllowedDropTrailerField(field: string): boolean {
    if (this.carrierEditableBaseFields.has(field)) {
      return true;
    }

    return this.workflowFlagConfigs.some((config) => {
      return field === config.field
        || field === this.timelineAtField(config)
        || field === config.dateField
        || field === config.timeField;
    });
  }

  private isDropTrailerColumnAvailable(colId: string): boolean {
    if (colId === 'work_order_details') return this.canOpenWorkOrderDetails();
    if (!this.canAccessAdminFlags() && (colId === 'drop_trailer_admin_flags' || this.isAdminFlagField(colId))) return false;
    return true;
  }

  private isAdminFlagField(field: string): boolean {
    return this.adminFlagConfigs.some((config) => {
      return field === config.field
        || field === this.timelineAtField(config)
        || field === config.dateField
        || field === config.timeField;
    });
  }

  private detailsCol(): ColDef<DropTrailer> {
    return {
      headerName: '',
      colId: 'work_order_details',
      width: 52,
      minWidth: 48,
      maxWidth: 58,
      pinned: 'left',
      lockPosition: 'left',
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: false,
      suppressMovable: true,
      cellClass: 'dt-details-cell',
      cellRenderer: (params: any) => {
        const ladingUuid = params.data?.lading_uuid;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dt-details-button';
        button.disabled = !ladingUuid;
        button.title = 'Open work order details';
        button.setAttribute('aria-label', 'Open work order details');
        button.innerHTML = '<i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openWorkOrderDetails(ladingUuid);
        });

        return button;
      },
    };
  }

  private workflowFlagsCol(): ColDef<DropTrailer> {
    return {
      headerName: 'Trailer Flow',
      colId: 'drop_trailer_flow',
      width: 270,
      minWidth: 220,
      maxWidth: 340,
      pinned: 'left',
      sortable: false,
      filter: 'agTextColumnFilter',
      floatingFilter: false,
      resizable: true,
      cellClass: ['dt-flags-cell', 'dt-flow-cell'],
      tooltipValueGetter: (params) => this.flagSummary(params.data, this.workflowFlagConfigs),
      filterValueGetter: (params) => this.flagSummary(params.data, this.workflowFlagConfigs),
      cellRenderer: DropTrailerFlagsCellRendererComponent,
      cellRendererParams: {
        flagsConfig: this.workflowFlagConfigs,
        showProgress: true,
        onFlagToggle: (event: any) => this.onDropTrailerFlagToggle(event),
        onFlowManage: (event: any) => this.openDropTrailerFlowManager(event.data, event.node),
      },
    };
  }

  private adminFlagsCol(): ColDef<DropTrailer> {
    return {
      headerName: 'Admin Flags',
      colId: 'drop_trailer_admin_flags',
      width: 520,
      minWidth: 360,
      maxWidth: 620,
      sortable: false,
      filter: 'agTextColumnFilter',
      floatingFilter: false,
      resizable: true,
      cellClass: 'dt-flags-cell',
      tooltipValueGetter: (params) => this.flagSummary(params.data, this.adminFlagConfigs),
      filterValueGetter: (params) => this.flagSummary(params.data, this.adminFlagConfigs),
      cellRenderer: DropTrailerFlagsCellRendererComponent,
      cellRendererParams: {
        flagsConfig: this.carrierLimitedMode
          ? this.adminFlagConfigs.map((flag) => ({ ...flag, readOnly: true }))
          : this.adminFlagConfigs,
        showProgress: false,
        onFlagToggle: (event: any) => this.onDropTrailerFlagToggle(event),
      },
    };
  }

  private hiddenFlagColumns(configs: DropTrailerFlagConfig[]): ColDef<DropTrailer>[] {
    return configs.filter((config) => config.valueType !== 'timestamp').map((config) => ({
      headerName: config.label,
      field: config.field,
      colId: config.field,
      hide: true,
      cellDataType: 'boolean',
      filter: 'agSetColumnFilter',
      floatingFilter: false,
      width: 132,
      valueGetter: (params) => this.isDropTrailerFlagComplete(config, params.data as DropTrailer),
      valueFormatter: this.yesNoFormatter,
      filterValueGetter: (params) => this.isDropTrailerFlagComplete(config, params.data as DropTrailer) ? 'Yes' : 'No',
      cellEditor: (config.readOnly || !this.canEditDropTrailerField(config.field)) ? undefined : 'agCheckboxCellEditor',
      cellRenderer: (config.readOnly || !this.canEditDropTrailerField(config.field)) ? undefined : 'agCheckboxCellRenderer',
      editable: !config.readOnly && this.canEditDropTrailerField(config.field),
      valueSetter: (params) => {
        (params.data as any)[config.field] = this.isTrue(params.newValue);
        return true;
      },
      cellClass: (config.readOnly || !this.canEditDropTrailerField(config.field)) ? 'dt-text-cell' : 'dt-editable-cell',
      enableRowGroup: true,
      enablePivot: true,
      suppressColumnsToolPanel: true,
    }));
  }

  private textCol(headerName: string, field: string, overrides: ColDef<DropTrailer> = {}): ColDef<DropTrailer> {
    return {
      headerName,
      field,
      colId: field,
      minWidth: 126,
      filter: 'agTextColumnFilter',
      tooltipField: field,
      cellClass: 'dt-text-cell',
      ...overrides,
    };
  }

  private editableTextCol(headerName: string, field: string, overrides: ColDef<DropTrailer> = {}): ColDef<DropTrailer> {
    return this.textCol(headerName, field, {
      editable: (params) => !!params.data?.lading_uuid && this.canEditDropTrailerField(field),
      cellClass: 'dt-editable-cell',
      ...overrides,
    });
  }

  private dateCol(headerName: string, field: string, overrides: ColDef<DropTrailer> = {}): ColDef<DropTrailer> {
    return {
      ...this.textCol(headerName, field, {
        width: 132,
        minWidth: 118,
        filter: 'agDateColumnFilter',
        valueGetter: (params) => this.normalizeDateInput((params.data as any)?.[field]),
        valueFormatter: this.dateFormatter,
        cellRenderer: (params: any) => this.renderDateBadge(params.value),
        cellClass: 'dt-date-cell',
      }),
      ...overrides,
    };
  }

  private editableDateCol(headerName: string, field: string, overrides: ColDef<DropTrailer> = {}): ColDef<DropTrailer> {
    return this.dateCol(headerName, field, {
      editable: (params) => !!params.data?.lading_uuid && this.canEditDropTrailerField(field),
      cellEditor: 'agDateStringCellEditor',
      singleClickEdit: true,
      valueParser: (params) => this.normalizeDateInput(params.newValue),
      valueSetter: (params) => {
        const nextValue = this.normalizeDateInput(params.newValue);
        const currentValue = this.normalizeDateInput((params.data as any)?.[field]);
        if (nextValue === currentValue) {
          return false;
        }

        (params.data as any)[field] = nextValue || null;
        return true;
      },
      cellClass: ['dt-date-cell', 'dt-editable-cell'],
      ...overrides,
    });
  }

  private appointmentWindowCol(): ColDef<DropTrailer> {
    return {
      headerName: 'Appt Window',
      colId: 'appointment_window',
      width: 178,
      minWidth: 158,
      filter: 'agTextColumnFilter',
      floatingFilter: false,
      sortable: true,
      resizable: true,
      cellClass: 'dt-window-cell',
      valueGetter: (params) => this.appointmentWindowSortValue(params.data as DropTrailer),
      tooltipValueGetter: (params) => this.appointmentWindowTooltip(params.data as DropTrailer),
      filterValueGetter: (params) => this.appointmentWindowTooltip(params.data as DropTrailer),
      cellRenderer: (params: any) => this.renderAppointmentWindow(params.data as DropTrailer),
      onCellClicked: (params) => this.openAppointmentWindowDialog(params.data as DropTrailer, params.node),
    };
  }

  private partnerCarrierCol(): ColDef<DropTrailer> {
    return this.textCol('Partner Carrier', 'interline_carrier_name', {
      width: 210,
      minWidth: 180,
      filter: 'agSetColumnFilter',
      editable: (params) => !!params.data?.lading_uuid && this.canEditDropTrailerField('interline_carrier_name'),
      cellClass: 'dt-editable-cell',
      singleClickEdit: true,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      cellEditorSelector: () => ({
        component: DropTrailerPartnerCarrierCellEditorComponent,
        params: {
          carrierLimitedMode: this.carrierLimitedMode,
        },
      }),
      valueGetter: (params) => (params.data as any)?.interline_carrier_name || '',
      valueSetter: (params) => this.setPartnerCarrierValue(params),
      tooltipValueGetter: (params) => this.partnerCarrierTooltip(params.data as DropTrailer),
      filterValueGetter: (params) => (params.data as any)?.interline_carrier_name || '',
    });
  }

  private numberCol(headerName: string, field: string, overrides: ColDef<DropTrailer> = {}): ColDef<DropTrailer> {
    return {
      headerName,
      field,
      colId: field,
      width: 112,
      minWidth: 96,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      aggFunc: 'sum',
      valueGetter: (params) => this.toNumber((params.data as any)?.[field]),
      valueFormatter: (params) => params.value === null || params.value === undefined ? '' : Number(params.value).toLocaleString(),
      cellClass: 'dt-number-cell',
      ...overrides,
    };
  }

  private statusCol(): ColDef<DropTrailer> {
    return {
      headerName: 'Drop Status',
      field: 'current_drop_status',
      colId: 'current_drop_status',
      width: 132,
      minWidth: 112,
      filter: 'agSetColumnFilter',
      cellClass: 'dt-status-cell',
      tooltipField: 'current_drop_status',
      cellRenderer: (params: any) => {
        const value = String(params.value || 'Open').trim();
        const pill = document.createElement('span');
        const label = value || 'Open';
        pill.className = `dt-status-pill dt-status-pill--${this.statusToneClass(label)}`;

        const dot = document.createElement('span');
        dot.className = 'dt-status-dot';

        const text = document.createElement('span');
        text.className = 'dt-status-text';
        text.textContent = label;

        pill.append(dot, text);
        return pill;
      },
    };
  }

  private ladingNotesCol(): ColDef<DropTrailer> {
    return {
      headerName: 'Note',
      colId: 'lading_notes',
      width: 260,
      minWidth: 220,
      flex: 0.9,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: false,
      cellClass: 'dt-lading-notes-cell',
      valueGetter: (params) => this.latestLadingNoteText(params.data as DropTrailer),
      filterValueGetter: (params) => this.latestLadingNoteText(params.data as DropTrailer),
      tooltipValueGetter: (params) => this.latestLadingNoteText(params.data as DropTrailer) || 'Open notes',
      cellRenderer: (params: any) => this.renderLadingNotesButton(params.data as DropTrailer),
    };
  }

  private renderDateBadge(value: any): HTMLElement {
    const badge = document.createElement('span');
    const normalizedDate = this.normalizeDateInput(value);
    const displayValue = this.formatDateDisplay(normalizedDate);

    badge.className = normalizedDate ? 'dt-date-badge' : 'dt-date-badge dt-date-badge--empty';
    badge.textContent = displayValue || 'Not set';

    return badge;
  }

  private renderAppointmentWindow(row: DropTrailer | undefined): HTMLElement {
    const windowInfo = this.appointmentWindowDisplay(row);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = windowInfo.empty
      ? 'dt-window-pill dt-window-pill--empty'
      : 'dt-window-pill';
    button.setAttribute('aria-label', windowInfo.tooltip);

    const content = document.createElement('span');
    content.className = 'dt-window-content';

    const title = document.createElement('span');
    title.className = 'dt-window-main';
    title.textContent = windowInfo.title;

    const sub = document.createElement('span');
    sub.className = 'dt-window-sub';
    sub.textContent = windowInfo.subtitle;

    content.append(title, sub);
    button.append(content);

    return button;
  }

  private renderLadingNotesButton(row: DropTrailer | undefined): HTMLElement {
    const noteText = this.latestLadingNoteText(row);
    const noteCount = this.ladingNoteCount(row);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = noteText ? 'dt-lading-notes-button' : 'dt-lading-notes-button dt-lading-notes-button--empty';
    button.title = noteText || 'No notes. Click to add one.';
    button.setAttribute('aria-label', noteText ? `Open notes. Latest note: ${noteText}` : 'Open notes');

    const icon = document.createElement('span');
    icon.className = 'dt-lading-notes-icon';
    icon.innerHTML = '<i class="fa-solid fa-note-sticky" aria-hidden="true"></i>';

    const content = document.createElement('span');
    content.className = 'dt-lading-notes-content';

    const title = document.createElement('span');
    title.className = 'dt-lading-notes-main';
    title.textContent = noteText ? this.truncateText(noteText, 68) : 'No notes';

    const sub = document.createElement('span');
    sub.className = 'dt-lading-notes-sub';
    sub.textContent = noteCount > 0
      ? `${noteCount} ${noteCount === 1 ? 'note' : 'notes'}`
      : noteText ? 'Latest note' : 'Click to add';

    content.append(title, sub);
    button.append(icon, content);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openLadingNotes(row);
    });

    return button;
  }

  private appointmentWindowDisplay(row: DropTrailer | undefined): {
    title: string;
    subtitle: string;
    tooltip: string;
    empty: boolean;
  } {
    const rowData = row as any;
    const startDate = this.normalizeDateInput(rowData?.appointment_start);
    const startTime = this.normalizeTimeInput(rowData?.appointment_start);
    const endDate = this.normalizeDateInput(rowData?.appointment_end);
    const endTime = this.normalizeTimeInput(rowData?.appointment_end);

    if (!startDate && !endDate) {
      const dropDate = this.formatDateDisplay(this.normalizeDateInput(rowData?.appointment_date));
      return {
        title: 'Schedule',
        subtitle: dropDate ? `Est. ${dropDate}` : 'No window',
        tooltip: 'Appointment window not set. Click to set the date and time range.',
        empty: true,
      };
    }

    if (startDate && endDate && startDate === endDate) {
      const timeRange = [this.formatTimeDisplay(startTime), this.formatTimeDisplay(endTime)]
        .filter(Boolean)
        .join(' - ');
      const title = this.formatDateDisplay(startDate);
      const subtitle = timeRange || 'Time not set';

      return {
        title,
        subtitle,
        tooltip: `${title} ${subtitle}`.trim(),
        empty: false,
      };
    }

    const startLabel = [this.formatDateDisplay(startDate), this.formatTimeDisplay(startTime)].filter(Boolean).join(' ');
    const endLabel = [this.formatDateDisplay(endDate), this.formatTimeDisplay(endTime)].filter(Boolean).join(' ');
    const title = startLabel || 'Start not set';
    const subtitle = endLabel ? `to ${endLabel}` : 'End not set';

    return {
      title,
      subtitle,
      tooltip: `${title} ${subtitle}`,
      empty: false,
    };
  }

  private appointmentWindowSortValue(row: DropTrailer | undefined): string {
    const rowData = row as any;
    return this.normalizeDateTimeInput(rowData?.appointment_start)
      || this.normalizeDateTimeInput(rowData?.appointment_end)
      || '';
  }

  private appointmentWindowTooltip(row: DropTrailer | undefined): string {
    return this.appointmentWindowDisplay(row).tooltip;
  }

  private statusToneClass(value: string): string {
    const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!normalized || normalized === 'open') return 'open';
    if (normalized.includes('finished') || normalized.includes('complete') || normalized.includes('closed')) return 'complete';
    if (normalized.includes('picked')) return 'picked';
    if (normalized.includes('pool')) return 'pool';
    if (normalized.includes('loaded')) return 'loaded';
    if (normalized.includes('dropped')) return 'dropped';
    return 'active';
  }

  fetchAndApplyGridState(): void {
    this.gridStateService.getUserGridState('drop_trailers').subscribe((state) => {
      this.initialState = this.withDropTrailerLayoutState(state || dropTrailerDefaultGridState);
      this.gridReady = true;
    }, error => {
      console.error('Failed to fetch grid state:', error);
      this.initialState = this.withDropTrailerLayoutState(dropTrailerDefaultGridState);
      this.gridReady = true;
    });
  }

  onStateUpdated(params: StateUpdatedEvent<DropTrailer>): void {
    if (params.sources.some((source) => this.gridStateSourcesToPersist.has(source))) {
      this.gridStateService.saveUserGridState('drop_trailers', params.state);
    }
  }

  refreshData(): void {
    this.loadDropTrailers();
    this.loadAnalytics();
  }

  resetSavedView(): void {
    const defaultState = this.withDropTrailerLayoutState(dropTrailerDefaultGridState);
    this.gridStateService.saveUserGridState('drop_trailers', defaultState as any);
    this.initialState = defaultState;
    this.activeInsightKey = '';
    this.gridApi?.resetColumnState();
    this.gridApi?.setFilterModel(null);
    this.gridApi?.closeToolPanel();
    this.universalFilter = '';
    this.gridApi?.setGridOption('quickFilterText', '');
    this.gridApi?.onFilterChanged();
  }

  private withDropTrailerLayoutState(state: any): any {
    const nextState = JSON.parse(JSON.stringify(state || dropTrailerDefaultGridState));
    const existingOrder = (nextState?.columnOrder?.orderedColIds || [])
      .map((colId: string) => colId === 'trip_number' ? 'lading_number' : colId)
      .filter((colId: string) => this.isDropTrailerColumnAvailable(colId));
    const preferredOrder = this.preferredDropTrailerColumnOrder.filter((colId) => this.isDropTrailerColumnAvailable(colId));
    const hiddenFlagColumnIds = [
      ...this.workflowFlagConfigs,
      ...(this.canAccessAdminFlags() ? this.adminFlagConfigs : []),
    ]
      .filter((config) => config.valueType !== 'timestamp')
      .map((config) => config.field);
    const remainingOrder = existingOrder.filter((colId: string) => !preferredOrder.includes(colId));

    nextState.columnOrder = {
      ...(nextState.columnOrder || {}),
      orderedColIds: [...preferredOrder, ...remainingOrder],
    };

    nextState.columnPinning = {
      ...(nextState.columnPinning || {}),
      leftColIds: this.frozenDropTrailerColumnIds.filter((colId) => this.isDropTrailerColumnAvailable(colId)),
      rightColIds: (nextState.columnPinning?.rightColIds || [])
        .filter((colId: string) => !this.frozenDropTrailerColumnIds.includes(colId) && this.isDropTrailerColumnAvailable(colId)),
    };

    nextState.columnVisibility = {
      ...(nextState.columnVisibility || {}),
      hiddenColIds: Array.from(new Set([
        ...(nextState.columnVisibility?.hiddenColIds || []),
        ...hiddenFlagColumnIds,
        'drop_trailer_status_note',
        ...(this.canOpenWorkOrderDetails() ? [] : ['work_order_details']),
      ])).filter((colId) => !this.requiredDropTrailerColumnIds.includes(colId)),
    };

    return nextState;
  }

  toggleColumnsPanel(): void {
    const api: any = this.gridApi;
    if (!api) return;

    if (api.getOpenedToolPanel?.() === 'columns') {
      api.closeToolPanel?.();
      return;
    }

    api.openToolPanel?.('columns');
  }

  exportGrid(): void {
    const api: any = this.gridApi;
    if (!api) return;

    const dateStamp = new Date().toISOString().slice(0, 10);
    const fileName = `drop-trailers-${dateStamp}.xlsx`;

    if (typeof api.exportDataAsExcel === 'function') {
      api.exportDataAsExcel({
        fileName,
        sheetName: 'Drop Trailers',
        allColumns: false,
      });
      return;
    }

    api.exportDataAsCsv?.({
      fileName: fileName.replace('.xlsx', '.csv'),
      allColumns: false,
    });
  }

  applyInsightFilter(key: string): void {
    this.activeInsightKey = key === 'total' || this.activeInsightKey === key ? '' : key;
    this.gridApi?.onFilterChanged();
  }

  isInsightActive(key: string): boolean {
    return key === 'total' ? !this.activeInsightKey : this.activeInsightKey === key;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.addEventListener('firstDataRendered', () => {
      this.ensureDetailsColumnFirst();
      this.autoSizeAll(false);
    });
    this.loadDropTrailers();
  }

  private ensureDetailsColumnFirst(): void {
    const columnIds = this.gridApi?.getColumns()?.map((column) => column.getId()) ?? [];
    if (!columnIds.includes('work_order_details')) return;

    this.gridApi.applyColumnState({
      state: [
        { colId: 'work_order_details', pinned: 'left' },
        ...columnIds
          .filter((colId) => colId !== 'work_order_details')
          .map((colId) => ({ colId })),
      ],
      applyOrder: true,
    });
  }

  autoSizeAll(skipHeader: boolean): void {
    const allColumnIds: string[] = [];
    this.gridApi.getColumns()?.forEach((column) => {
      allColumnIds.push(column.getId());
    });

    if (!allColumnIds.length) return;

    this.gridApi.autoSizeColumns(allColumnIds, skipHeader);
  }


  private moveCompletedDropTrailerRowsToBottom(nodes: any[]): void {
    if (!Array.isArray(nodes) || nodes.length < 2) return;

    const openRows = nodes.filter((node) => !this.isDropTrailerFlowComplete(node?.data as DropTrailer));
    const completeRows = nodes.filter((node) => this.isDropTrailerFlowComplete(node?.data as DropTrailer));
    nodes.splice(0, nodes.length, ...openRows, ...completeRows);
  }

  private isDropTrailerFlowComplete(row: DropTrailer | undefined): boolean {
    return !!row && this.workflowFlagConfigs.every((config) => this.isDropTrailerFlagComplete(config, row));
  }

  private loadDropTrailers(): void {
    this.loading = true;
    this.errorMessage = '';

    this.http.get<any>(this.dropTrailerPageUrl(0)).subscribe({
      next: (data) => {
        const firstPageRows = data?.data ?? [];
        const totalRows = Number(data?.totalRows ?? firstPageRows.length);
        this.applyEditableFields(data?.editableFields);

        if (totalRows <= this.dropTrailerPageSize) {
          this.applyDropTrailerRows(firstPageRows);
          return;
        }

        const pageRequests: Array<Observable<any>> = [];
        for (let startRow = this.dropTrailerPageSize; startRow < totalRows; startRow += this.dropTrailerPageSize) {
          pageRequests.push(this.http.get<any>(this.dropTrailerPageUrl(startRow)));
        }

        forkJoin(pageRequests).subscribe({
          next: (pages) => {
            const rows = pages.reduce<DropTrailer[]>((allRows, page) => {
              return allRows.concat(page?.data ?? []);
            }, firstPageRows);
            this.applyDropTrailerRows(rows);
          },
          error: (err) => this.handleDropTrailerLoadError(err)
        });
      },
      error: (err) => this.handleDropTrailerLoadError(err)
    });
  }

  private dropTrailerPageUrl(startRow: number): string {
    const params = new URLSearchParams({
      startRow: String(startRow),
      endRow: String(startRow + this.dropTrailerPageSize),
      filters: '',
      filterModel: '',
      sortModel: '',
      universalFilter: ''
    });
    return `${this.apiUrl}?${params.toString()}`;
  }

  private loadAnalytics(): void {
    this.analyticsLoading = true;
    this.analyticsError = '';

    this.http.get<DropTrailerAnalyticsResponse>(this.dropTrailerAnalyticsUrl()).subscribe({
      next: (response) => {
        this.analyticsLoading = false;

        if (!response || response.success !== true) {
          this.analyticsError = 'Analytics unavailable';
          console.warn('Drop trailer analytics rejected:', response);
          this.refreshInsights();
          return;
        }

        this.dropTrailerAnalytics = response;
        this.refreshInsights();
      },
      error: (error) => {
        this.analyticsLoading = false;
        this.analyticsError = 'Analytics unavailable';
        console.warn('Drop trailer analytics load failed:', error);
        this.refreshInsights();
      },
    });
  }

  private dropTrailerAnalyticsUrl(): string {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    const params = new URLSearchParams({
      timezone,
      year: String(now.getFullYear()),
      month,
      include_active: 'true',
    });

    return `${this.analyticsApiUrl}?${params.toString()}`;
  }

  private applyDropTrailerRows(rows: DropTrailer[]): void {
    this.loading = false;
    this.rowData = rows;
    this.refreshInsights();
    this.gridApi?.setGridOption('rowData', this.rowData);
    this.gridApi?.setGridOption('quickFilterText', this.universalFilter);
    this.gridApi?.onFilterChanged();
  }

  private applyEditableFields(fields: unknown): void {
    if (!Array.isArray(fields) || fields.length === 0) {
      return;
    }

    this.editableDropTrailerFields.clear();
    fields.forEach((field) => {
      if (typeof field === 'string' && field.trim() !== '') {
        const normalizedField = field.trim() === 'trip_number' ? 'lading_number' : field.trim();
        this.editableDropTrailerFields.add(normalizedField);
      }
    });
  }

  private handleDropTrailerLoadError(err: any): void {
    this.loading = false;
    this.errorMessage = 'Network error loading drop trailers.';
    this.rowData = [];
    this.dropTrailerInsights = [];
    console.error('Error fetching drop trailers:', err);
  }

  private onDropTrailerCellValueChanged(event: CellValueChangedEvent<DropTrailer>): void {
    if (this.revertingDropTrailerCell) {
      return;
    }

    const field = event.colDef.field;
    const rowUuid = event.data?.lading_uuid;

    if (!field || !rowUuid || !this.canEditDropTrailerField(field)) {
      return;
    }

    if (this.areGridValuesEqual(event.oldValue, event.newValue)) {
      return;
    }

    if (field === 'interline_carrier_name') {
      this.savePartnerCarrierValue(event);
      return;
    }

    this.saveDropTrailerField({
      row: event.data,
      field,
      value: this.normalizeDropTrailerUpdateValue(field, event.newValue),
      node: event.node,
      oldValue: event.oldValue,
      revertOnFailure: true,
    });
  }

  private onDropTrailerFlagToggle(event: {
    field: string;
    value: boolean;
    flag: DropTrailerFlagConfig;
    data: DropTrailer;
    node: any;
  }): void {
    if (!event.data?.lading_uuid || !event.field || !this.canEditDropTrailerField(event.field)) {
      this.toastr.error('That drop trailer flag is not editable.');
      return;
    }

    const workflowIndex = this.workflowFlagConfigs.findIndex((config) => config.field === event.field);
    const isWorkflowFlag = workflowIndex >= 0;
    const options: Record<string, any> = {};

    if (isWorkflowFlag) {
      const currentStep = this.workflowFlagConfigs[workflowIndex];

      if (event.value) {
        const missingPreviousSteps = this.workflowFlagConfigs
          .slice(0, workflowIndex)
          .filter((config) => !this.isTrue((event.data as any)[config.field]));

        this.promptDropTrailerFlowDate({
          row: event.data,
          node: event.node,
          flag: currentStep,
          missingPreviousSteps,
        });
        return;
      } else {
        const completedLaterSteps = this.workflowFlagConfigs
          .slice(workflowIndex + 1)
          .filter((config) => this.isTrue((event.data as any)[config.field]));

        if (completedLaterSteps.length) {
          this.confirmDropTrailerFlowChange({
            title: 'Clear Later Steps?',
            message: `Clear "${currentStep.label}"? This will also clear these later steps: ${completedLaterSteps.map((step) => step.label).join(', ')}.`,
            confirmText: 'Clear Steps',
            onConfirm: () => {
              this.saveDropTrailerChanges({
                row: event.data,
                changes: this.buildTimelineClearingChanges([currentStep, ...completedLaterSteps]),
                node: event.node,
                options,
              });
            },
          });
          return;
        }

        this.saveDropTrailerChanges({
          row: event.data,
          changes: this.buildTimelineClearingChanges([currentStep]),
          node: event.node,
          options,
        });
        return;
      }
    }

    if (event.flag.valueType === 'timestamp') {
      if (event.value) {
        this.promptTimestampFlagDate({
          row: event.data,
          node: event.node,
          flag: event.flag,
        });
        return;
      }

      this.saveDropTrailerField({
        row: event.data,
        field: event.field,
        value: null,
        node: event.node,
        options,
      });
      return;
    }

    this.saveDropTrailerField({
      row: event.data,
      field: event.field,
      value: event.value,
      node: event.node,
      options,
    });
  }

  private promptDropTrailerFlowDate(args: {
    row: DropTrailer;
    node: any;
    flag: DropTrailerFlagConfig;
    missingPreviousSteps: DropTrailerFlagConfig[];
  }): void {
    const atField = this.timelineAtField(args.flag);
    const row = args.row as any;
    const initialDate = this.normalizeDateInput(row[args.flag.dateField || ''])
      || this.normalizeDateInput(row[atField]);
    const initialTime = this.normalizeTimeInput(row[args.flag.timeField || ''])
      || this.normalizeTimeInput(row[atField]);
    const leapfrogMessage = args.missingPreviousSteps.length
      ? `This will also complete: ${args.missingPreviousSteps.map((step) => step.label).join(', ')}.`
      : '';

    const dialogRef = this.dialog.open<
      DropTrailerFlowDateDialogComponent,
      DropTrailerFlowDateDialogData,
      DropTrailerFlowDateDialogResult | null
    >(DropTrailerFlowDateDialogComponent, {
      width: '440px',
      data: {
        title: 'Mark Milestone Complete',
        label: args.flag.label,
        message: leapfrogMessage,
        initialDate,
        initialTime,
        confirmText: 'Mark Complete',
      },
      autoFocus: true,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const flowFlagsToComplete = [...args.missingPreviousSteps, args.flag];
      const changes = this.buildTimelineCompletionChangesForFlags(flowFlagsToComplete, result);
      const options = {};
      const statusUpdates = this.ladingStatusUpdatesForCompletedFlowFlags(
        args.row,
        flowFlagsToComplete,
        result,
      );

      this.saveDropTrailerChanges({
        row: args.row,
        changes,
        node: args.node,
        options,
        statusUpdates,
      });
    });
  }

  private promptTimestampFlagDate(args: {
    row: DropTrailer;
    node: any;
    flag: DropTrailerFlagConfig;
  }): void {
    const row = args.row as any;
    const initialDate = this.normalizeDateInput(row[args.flag.field]);
    const initialTime = this.normalizeTimeInput(row[args.flag.field]);

    const dialogRef = this.dialog.open<
      DropTrailerFlowDateDialogComponent,
      DropTrailerFlowDateDialogData,
      DropTrailerFlowDateDialogResult | null
    >(DropTrailerFlowDateDialogComponent, {
      width: '440px',
      data: {
        eyebrow: 'Admin',
        title: args.flag.label,
        label: 'Enter the date and time for this update.',
        initialDate,
        initialTime,
        confirmText: 'Save',
      },
      autoFocus: true,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.saveDropTrailerField({
        row: args.row,
        field: args.flag.field,
        value: this.composeTimelineTimestamp(result.date, result.time),
        node: args.node,
      });
    });
  }

  private openDropTrailerFlowManager(row: DropTrailer | undefined, node: any): void {
    if (!row?.lading_uuid) {
      this.toastr.error('Unable to open trailer flow for this row.');
      return;
    }

    const dialogRef = this.dialog.open<
      DropTrailerFlowManagerDialogComponent,
      DropTrailerFlowManagerDialogData,
      DropTrailerFlowManagerDialogResult | null
    >(DropTrailerFlowManagerDialogComponent, {
      width: '660px',
      data: {
        row,
        flags: this.workflowFlagConfigs,
      },
      autoFocus: true,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const flowItems = result.items || [];
      const changes = this.buildFlowManagerChanges(row, flowItems);
      if (!Object.keys(changes).length) {
        this.toastr.info('No trailer flow changes to save.');
        return;
      }

      this.saveDropTrailerChanges({
        row,
        changes,
        node,
        statusUpdates: this.ladingStatusUpdatesForFlowManagerItems(row, flowItems),
      });
    });
  }

  private openAppointmentWindowDialog(row: DropTrailer | undefined, node: any): void {
    if (!row?.lading_uuid) {
      this.toastr.error('Unable to open appointment window for this row.');
      return;
    }

    if (!this.canEditDropTrailerField('appointment_start') || !this.canEditDropTrailerField('appointment_end')) {
      this.toastr.error('Appointment window is not editable for this drop trailer.');
      return;
    }

    const dialogRef = this.dialog.open<
      DropTrailerAppointmentWindowDialogComponent,
      DropTrailerAppointmentWindowDialogData,
      DropTrailerAppointmentWindowDialogResult | null
    >(DropTrailerAppointmentWindowDialogComponent, {
      width: '540px',
      data: {
        title: 'Appointment Window',
        orderLabel: this.dropTrailerRowLabel(row),
        initialStart: (row as any).appointment_start,
        initialEnd: (row as any).appointment_end,
        fallbackDate: (row as any).appointment_date,
      },
      autoFocus: true,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const nextStart = result.start ? this.normalizeDateTimeInput(result.start) : null;
      const nextEnd = result.end ? this.normalizeDateTimeInput(result.end) : null;
      const currentStart = this.normalizeDateTimeInput((row as any).appointment_start) || null;
      const currentEnd = this.normalizeDateTimeInput((row as any).appointment_end) || null;

      if (nextStart === currentStart && nextEnd === currentEnd) {
        this.toastr.info('No appointment window changes to save.');
        return;
      }

      this.saveDropTrailerChanges({
        row,
        node,
        changes: {
          appointment_start: nextStart,
          appointment_end: nextEnd,
        },
      });
    });
  }

  private openWorkOrderDetails(ladingUuid: string | undefined): void {
    if (!ladingUuid) {
      this.toastr.error('Unable to open work order details for this row.');
      return;
    }

    this.router.navigate(['/work_order_details', ladingUuid]);
  }

  private openLadingNotes(row: DropTrailer | undefined): void {
    if (!row?.lading_uuid) {
      this.toastr.error('Unable to open notes for this row.');
      return;
    }

    const dialogRef = this.dialog.open(DropTrailerNotesModalComponent, {
      width: '800px',
      maxHeight: '85vh',
      data: {
        lading_uuid: row.lading_uuid,
        rowData: row,
        title: 'Notes',
      },
      autoFocus: false,
      panelClass: 'ulaap-modal-panel',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.refreshGrid) {
        this.refreshData();
      }
    });
  }

  private dropTrailerRowLabel(row: DropTrailer): string {
    const labelParts = [
      row.alternate_reference,
      (row as any).lading_number ? `BOL ${(row as any).lading_number}` : (row.trip_number ? `BOL ${row.trip_number}` : ''),
      row.carrier_trailer ? `Trailer ${row.carrier_trailer}` : '',
    ].filter((value) => String(value || '').trim() !== '');

    return labelParts.length ? labelParts.join(' / ') : row.lading_uuid;
  }

  private confirmDropTrailerFlowChange(args: ConfirmDialogData & { onConfirm: () => void }): void {
    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
      width: '420px',
      data: {
        title: args.title,
        message: args.message,
        confirmText: args.confirmText,
        cancelText: args.cancelText || 'Cancel',
        tone: args.tone || 'warning',
      },
      autoFocus: true,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        args.onConfirm();
      }
    });
  }

  private saveDropTrailerField(args: {
    row: DropTrailer;
    field: string;
    value: any;
    node?: any;
    oldValue?: any;
    revertOnFailure?: boolean;
    options?: Record<string, any>;
  }): void {
    const rowUuid = args.row?.lading_uuid;
    if (!rowUuid) return;

    const payload = {
      lading_uuid: rowUuid,
      field: args.field,
      value: args.value,
      row: args.row,
      ...(args.options ?? {}),
    };

    this.http.put<any>(this.apiUrl, payload).subscribe({
      next: (response) => {
        if (!this.isDropTrailerResponseSuccess(response)) {
          if (args.revertOnFailure && args.node) {
            this.revertDropTrailerNodeValue(args.node, args.field, args.oldValue);
          }
          this.toastr.error(response?.message || response?.error || 'Drop trailer update failed.');
          return;
        }

        const updatedRow = {
          ...args.row,
          [args.field]: args.value,
          ...(response?.row ?? {}),
        };

        args.node?.setData(updatedRow);
        this.rowData = this.rowData.map((row) => row.lading_uuid === rowUuid ? { ...row, ...updatedRow } : row);
        this.refreshInsights();
        this.toastr.success('Drop trailer updated.');
      },
      error: (error) => {
        if (args.revertOnFailure && args.node) {
          this.revertDropTrailerNodeValue(args.node, args.field, args.oldValue);
        }
        this.toastr.error(error?.error?.message || 'Drop trailer update failed.');
        console.error('Drop trailer update failed:', error);
      },
    });
  }

  private saveDropTrailerChanges(args: {
    row: DropTrailer;
    changes: Record<string, any>;
    node?: any;
    options?: Record<string, any>;
    statusUpdates?: DropTrailerLadingStatusSync[];
  }): void {
    const rowUuid = args.row?.lading_uuid;
    const changes = Object.entries(args.changes || {})
      .filter(([field]) => !!field)
      .reduce<Record<string, any>>((out, [field, value]) => {
        out[field] = value;
        return out;
      }, {});

    if (!rowUuid || Object.keys(changes).length === 0) return;

    const payload = {
      lading_uuid: rowUuid,
      changes,
      row: args.row,
      ...(args.options ?? {}),
    };

    this.http.put<any>(this.apiUrl, payload).subscribe({
      next: (response) => {
        if (!this.isDropTrailerResponseSuccess(response)) {
          this.toastr.error(response?.message || response?.error || 'Drop trailer update failed.');
          return;
        }

        const updatedRow = {
          ...args.row,
          ...changes,
          ...(response?.row ?? {}),
        };

        args.node?.setData(updatedRow);
        this.rowData = this.rowData.map((row) => row.lading_uuid === rowUuid ? { ...row, ...updatedRow } : row);
        this.refreshInsights();
        this.syncDropTrailerLadingStatuses(updatedRow, args.statusUpdates, args.node);
        this.toastr.success('Drop trailer updated.');
      },
      error: (error) => {
        this.toastr.error(error?.error?.message || 'Drop trailer update failed.');
        console.error('Drop trailer update failed:', error);
      },
    });
  }

  private syncDropTrailerLadingStatuses(
    row: DropTrailer,
    statusUpdates: DropTrailerLadingStatusSync[] | undefined,
    node?: any,
  ): void {
    const rowUuid = row?.lading_uuid;
    const updates = this.uniqueLadingStatusUpdates(statusUpdates || []);
    if (!rowUuid || updates.length === 0) return;

    let failedStatusCount = 0;
    const requests = updates.map((update) => {
      return this.http.post<any>(
        '/api/lading_status/add_lading_status.php',
        this.buildLadingStatusPayload(rowUuid, update),
      );
    });

    concat(...requests).subscribe({
      next: (response) => {
        if (!this.isLadingStatusResponseSuccess(response)) {
          failedStatusCount += 1;
          console.warn('Lading status sync failed:', response);
        }
      },
      error: (error) => {
        console.error('Lading status sync failed:', error);
        this.toastr.warning('Trailer flow saved, but lading status was not updated.');
      },
      complete: () => {
        if (failedStatusCount > 0) {
          this.toastr.warning('Trailer flow saved, but one or more lading statuses were not updated.');
          return;
        }

        const finalStatus = updates[updates.length - 1]?.status;
        if (finalStatus) {
          this.applyDropTrailerCurrentStatus(rowUuid, finalStatus, node);
        }
      },
    });
  }

  private buildLadingStatusPayload(rowUuid: string, update: DropTrailerLadingStatusSync): Record<string, any> {
    const payload: Record<string, any> = {
      lading_uuid: rowUuid,
      lading_status: update.status,
    };

    const selectedDate = this.formatLadingStatusDateForApi(update.selectedDate);
    const selectedTime = this.normalizeTimeInput(update.selectedTime);
    if (selectedDate && selectedTime) {
      payload['selected_date'] = selectedDate;
      payload['selected_time'] = selectedTime;
      payload['selected_timezone'] = this.defaultLadingStatusTimezone();
    }

    return payload;
  }

  private uniqueLadingStatusUpdates(statusUpdates: DropTrailerLadingStatusSync[]): DropTrailerLadingStatusSync[] {
    const seen = new Set<string>();
    return statusUpdates.filter((update) => {
      const key = [
        update.status,
        update.selectedDate || '',
        update.selectedTime || '',
      ].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private isLadingStatusResponseSuccess(response: any): boolean {
    const body = Array.isArray(response) ? response[0] : response;
    const code = Number(body?.code ?? body?.status ?? 200);
    return body?.success !== false && (!Number.isFinite(code) || code < 400);
  }

  private applyDropTrailerCurrentStatus(rowUuid: string, status: string, node?: any): void {
    if (node?.data) {
      node.setData({
        ...node.data,
        current_drop_status: status,
      });
    }

    this.rowData = this.rowData.map((row) => {
      return row.lading_uuid === rowUuid ? { ...row, current_drop_status: status } : row;
    });
  }

  private isDropTrailerResponseSuccess(response: any): boolean {
    return response?.success === true && Number(response?.code ?? 200) < 400;
  }

  private revertDropTrailerNodeValue(node: any, field: string, value: any): void {
    this.revertingDropTrailerCell = true;
    try {
      node.setDataValue(field, value);
    } finally {
      this.revertingDropTrailerCell = false;
    }
  }

  private normalizeDropTrailerUpdateValue(field: string, value: any): any {
    if (value === undefined) return null;

    if (value instanceof Date && Number.isFinite(value.getTime())) {
      return this.formatDateForApi(value);
    }

    if (this.isDateOnlyDropTrailerField(field)) {
      const normalizedDate = this.normalizeDateInput(value);
      return normalizedDate || null;
    }

    if (this.isBooleanDropTrailerField(field)) {
      return this.isTrue(value);
    }

    if (field === 'load_pieces') {
      return this.parseEditableNumber(value);
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      return trimmedValue === '' ? null : trimmedValue;
    }

    return value;
  }

  private savePartnerCarrierValue(event: CellValueChangedEvent<DropTrailer>): void {
    if (!event.data?.lading_uuid) return;

    const partnerValue = this.normalizePartnerCarrierEditValue(event.newValue);
    const structuredValue = this.isPartnerCarrierEditValue(event.newValue);
    const currentContactUuid = this.nullIfBlank((event.data as any).interline_carrier_contact_uuid);
    const previousContactUuid = structuredValue
      ? this.nullIfBlank((event.newValue as DropTrailerPartnerCarrierEditValue).previous_contact_uuid)
      : currentContactUuid;
    const nextName = partnerValue.name;
    const nextContactUuid = structuredValue ? partnerValue.contact_uuid : currentContactUuid;

    if (
      this.areGridValuesEqual(event.oldValue, nextName)
      && this.areGridValuesEqual(previousContactUuid, nextContactUuid)
    ) {
      return;
    }

    this.saveDropTrailerChanges({
      row: event.data,
      changes: {
        interline_carrier_name: nextName,
        interline_carrier_contact_uuid: nextContactUuid,
      },
      node: event.node,
    });
  }

  private setPartnerCarrierValue(params: any): boolean {
    const data = params.data as any;
    if (!data) return false;

    const nextValue = this.normalizePartnerCarrierEditValue(params.newValue);
    const currentName = this.nullIfBlank(data.interline_carrier_name);
    const currentContactUuid = this.nullIfBlank(data.interline_carrier_contact_uuid);
    const structuredValue = this.isPartnerCarrierEditValue(params.newValue);
    const nextContactUuid = structuredValue ? nextValue.contact_uuid : currentContactUuid;

    if (
      this.areGridValuesEqual(currentName, nextValue.name)
      && this.areGridValuesEqual(currentContactUuid, nextContactUuid)
    ) {
      return false;
    }

    data.interline_carrier_name = nextValue.name || '';
    data.interline_carrier_contact_uuid = nextContactUuid;
    return true;
  }

  private isPartnerCarrierEditValue(value: any): value is DropTrailerPartnerCarrierEditValue {
    return !!value && typeof value === 'object' && ('name' in value || 'contact_uuid' in value);
  }

  private normalizePartnerCarrierEditValue(value: any): DropTrailerPartnerCarrierEditValue {
    if (value && typeof value === 'object' && ('name' in value || 'contact_uuid' in value)) {
      return {
        name: this.nullIfBlank(value.name),
        contact_uuid: this.nullIfBlank(value.contact_uuid),
        relationship_uuid: this.nullIfBlank(value.relationship_uuid),
        is_override: !!value.is_override,
        previous_contact_uuid: this.nullIfBlank(value.previous_contact_uuid),
      };
    }

    return {
      name: this.nullIfBlank(value),
      contact_uuid: null,
    };
  }

  private partnerCarrierTooltip(row: DropTrailer | undefined): string {
    if (!row) return '';
    const rowData = row as any;
    const name = this.nullIfBlank(rowData.interline_carrier_name);
    const uuid = this.nullIfBlank(rowData.interline_carrier_contact_uuid);
    return [name, uuid ? `Contact ${uuid}` : ''].filter(Boolean).join(' | ');
  }

  private ladingStatusUpdatesForCompletedFlowFlags(
    row: DropTrailer,
    flags: DropTrailerFlagConfig[],
    result: DropTrailerFlowDateDialogResult,
  ): DropTrailerLadingStatusSync[] {
    return flags
      .filter((flag) => !this.isDropTrailerFlagComplete(flag, row))
      .map((flag) => this.ladingStatusUpdateForFlowField(flag.field, result.date, result.time))
      .filter((update): update is DropTrailerLadingStatusSync => !!update);
  }

  private ladingStatusUpdatesForFlowManagerItems(
    row: DropTrailer,
    items: DropTrailerFlowManagerItem[],
  ): DropTrailerLadingStatusSync[] {
    return items
      .filter((item) => {
        const flag = this.workflowFlagConfigs.find((config) => config.field === item.field);
        return !!flag && item.complete && !this.isDropTrailerFlagComplete(flag, row);
      })
      .map((item) => this.ladingStatusUpdateForFlowField(item.field, item.date, item.time))
      .filter((update): update is DropTrailerLadingStatusSync => !!update);
  }

  private ladingStatusUpdateForFlowField(
    field: string,
    selectedDate?: string,
    selectedTime?: string,
  ): DropTrailerLadingStatusSync | null {
    const status = this.ladingStatusByFlowField[field];
    if (!status) return null;

    return {
      status,
      selectedDate,
      selectedTime,
    };
  }

  private buildTimelineCompletionChangesForFlags(
    flags: DropTrailerFlagConfig[],
    result: DropTrailerFlowDateDialogResult
  ): Record<string, any> {
    return flags.reduce<Record<string, any>>((changes, flag) => {
      Object.assign(changes, this.buildTimelineCompletionChanges(flag, result));
      return changes;
    }, {});
  }

  private buildTimelineCompletionChanges(
    flag: DropTrailerFlagConfig,
    result: DropTrailerFlowDateDialogResult
  ): Record<string, any> {
    const normalizedTime = this.normalizeTimeInput(result.time) || '00:00';
    const timestamp = this.composeTimelineTimestamp(result.date, normalizedTime);
    const changes: Record<string, any> = {
      [this.timelineAtField(flag)]: timestamp,
    };

    if (flag.dateField) changes[flag.dateField] = result.date;
    if (flag.timeField) changes[flag.timeField] = normalizedTime;
    return changes;
  }

  private buildTimelineClearingChanges(flags: DropTrailerFlagConfig[]): Record<string, any> {
    return flags.reduce<Record<string, any>>((changes, flag) => {
      changes[flag.field] = false;
      changes[this.timelineAtField(flag)] = null;
      if (flag.dateField) changes[flag.dateField] = null;
      if (flag.timeField) changes[flag.timeField] = null;
      return changes;
    }, {});
  }

  private buildFlowManagerChanges(
    row: DropTrailer,
    items: DropTrailerFlowManagerItem[]
  ): Record<string, any> {
    const changes: Record<string, any> = {};
    const rowData = row as any;

    items.forEach((item) => {
      const flag = this.workflowFlagConfigs.find((config) => config.field === item.field);
      if (!flag) return;

      const originalComplete = this.isDropTrailerFlagComplete(flag, row);
      if (!item.complete) {
        if (originalComplete) {
          Object.assign(changes, this.buildTimelineClearingChanges([flag]));
        }
        return;
      }

      const nextTimestamp = this.composeTimelineTimestamp(item.date, item.time);
      const originalTimestamp = this.timelineComparableTimestamp(rowData, flag);
      if (!originalComplete || originalTimestamp !== nextTimestamp) {
        Object.assign(changes, this.buildTimelineCompletionChanges(flag, {
          date: item.date,
          time: item.time,
        }));
      }
    });

    return changes;
  }

  private timelineAtField(flag: DropTrailerFlagConfig): string {
    return `${flag.field}_at`;
  }

  private composeTimelineTimestamp(date: string, time: string): string {
    const normalizedTime = this.normalizeTimeInput(time) || '00:00';
    return `${date} ${normalizedTime}:00`;
  }

  private normalizeDateInput(value: any): string {
    if (value === null || value === undefined) return '';
    const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }

  private normalizeTimeInput(value: any): string {
    if (value === null || value === undefined) return '';
    const match = String(value).trim().match(/(?:^|\s|T)(\d{2}:\d{2})/);
    return match ? match[1] : '';
  }

  private normalizeDateTimeInput(value: any): string {
    const date = this.normalizeDateInput(value);
    const time = this.normalizeTimeInput(value);
    if (!date || !time) return '';

    return `${date} ${time}:00`;
  }

  private isDateOnlyDropTrailerField(field: string): boolean {
    return field === 'load_start' || field === 'appointment_date';
  }

  private formatDateDisplay(value: any): string {
    const normalizedDate = this.normalizeDateInput(value);
    if (!normalizedDate) return '';

    const match = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return String(value || '');

    return `${match[2]}/${match[3]}/${match[1]}`;
  }

  private formatTimeDisplay(value: any): string {
    const normalizedTime = this.normalizeTimeInput(value);
    if (!normalizedTime) return '';

    const [hourPart, minutePart] = normalizedTime.split(':');
    const hour = Number(hourPart);
    if (!Number.isFinite(hour)) return normalizedTime;

    const suffix = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutePart} ${suffix}`;
  }

  private timelineComparableTimestamp(row: Record<string, any>, flag: DropTrailerFlagConfig): string {
    const atField = this.timelineAtField(flag);
    const date = this.normalizeDateInput(row[flag.dateField || '']) || this.normalizeDateInput(row[atField]);
    const time = this.normalizeTimeInput(row[flag.timeField || '']) || this.normalizeTimeInput(row[atField]);
    return date && time ? this.composeTimelineTimestamp(date, time) : '';
  }

  private parseEditableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private nullIfBlank(value: any): string | null {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === '' ? null : normalized;
  }

  private areGridValuesEqual(oldValue: any, newValue: any): boolean {
    const normalize = (value: any) => value === undefined || value === null ? '' : String(value).trim();
    return normalize(oldValue) === normalize(newValue);
  }

  private isBooleanDropTrailerField(field: string): boolean {
    return [...this.workflowFlagConfigs, ...this.adminFlagConfigs].some((config) => {
      return config.field === field && config.valueType !== 'timestamp';
    });
  }

  private isDropTrailerFlagComplete(config: DropTrailerFlagConfig, row: DropTrailer | undefined): boolean {
    if (!row) return false;
    const rowData = row as any;
    const value = rowData[config.field];
    if (config.valueType === 'timestamp') {
      return value !== null && value !== undefined && String(value).trim() !== '';
    }

    if (this.isTrue(value)) {
      return true;
    }

    const atField = this.timelineAtField(config);
    return !!(
      this.normalizeDateTimeInput(rowData[atField])
      || (this.normalizeDateInput(rowData[config.dateField || '']) && this.normalizeTimeInput(rowData[config.timeField || '']))
    );
  }

  private matchesActiveInsight(row: DropTrailer | undefined): boolean {
    if (!row || !this.activeInsightKey) {
      return true;
    }

    switch (this.activeInsightKey) {
      case 'dc':
        return this.isTrue(row.dropped_dc) && !this.isTrue(row.picked_up_dc);
      case 'pool':
        return this.isTrue(row.dropped_pool) && !this.isTrue(row.pool_offload_finished);
      case 'scanned':
        return this.isTrue(row.pool_scanning_finished);
      default:
        return true;
    }
  }

  private flagSummary(row: DropTrailer | undefined, configs: DropTrailerFlagConfig[]): string {
    if (!row) return '';

    return configs
      .filter((config) => this.isDropTrailerFlagComplete(config, row))
      .map((config) => config.label)
      .join(', ');
  }

  private readonly yesNoFormatter = (params: ValueFormatterParams): string => {
    return this.isTrue(params.value) ? 'Yes' : 'No';
  };

  private latestLadingNoteText(row: DropTrailer | undefined): string {
    if (!row) return '';

    const directValue = this.firstNoteTextValue(row, [
      'latest_lading_note',
      'v_lading_notes.note',
      'lading_note',
      'notes',
      'note',
    ]);
    if (directValue) return directValue;

    const latestNote = this.latestLadingNoteFromArray(row);
    return latestNote ? this.noteTextFromUnknown(latestNote) : '';
  }

  private ladingNoteCount(row: DropTrailer | undefined): number {
    if (!row) return 0;

    const directCount = this.firstNumberValue(row, [
      'lading_note_count',
      'notes_count',
    ]);
    if (directCount !== null) return directCount;

    const notes = this.ladingNotesFromArray(row);
    return notes.length;
  }

  private latestLadingNoteFromArray(row: DropTrailer): any | null {
    const notes = this.ladingNotesFromArray(row);
    if (!notes.length) return null;

    return [...notes].sort((a, b) => this.noteSortValue(b) - this.noteSortValue(a))[0];
  }

  private ladingNotesFromArray(row: DropTrailer): any[] {
    const rawNotes = (row as any).lading_notes || (row as any).carrier_lading_notes || [];
    const parsedNotes = this.noteArrayFromUnknown(rawNotes);
    if (!parsedNotes.length) return [];

    return parsedNotes.filter((note) => !!this.noteTextFromUnknown(note));
  }

  private noteArrayFromUnknown(rawNotes: unknown): any[] {
    if (Array.isArray(rawNotes)) return rawNotes;

    if (typeof rawNotes === 'string') {
      const text = rawNotes.trim();
      if (!text) return [];

      const parsed = this.parseMaybeJson(text);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && this.noteTextFromUnknown(parsed)) return [parsed];
      return [text];
    }

    if (rawNotes && typeof rawNotes === 'object') {
      return this.noteTextFromUnknown(rawNotes) ? [rawNotes] : [];
    }

    return [];
  }

  private noteTextFromUnknown(rawNote: unknown): string {
    if (rawNote === null || rawNote === undefined) return '';

    if (Array.isArray(rawNote)) {
      const latestNote = [...rawNote]
        .filter((note) => !!this.noteTextFromUnknown(note))
        .sort((a, b) => this.noteSortValue(b) - this.noteSortValue(a))[0];
      return latestNote ? this.noteTextFromUnknown(latestNote) : '';
    }

    if (typeof rawNote === 'object') {
      return this.firstObjectStringValue(rawNote as Record<string, unknown>, [
        'note',
        'lading_note',
        'latest_lading_note',
        'text',
        'body',
        'comment',
      ]);
    }

    if (typeof rawNote === 'string') {
      const text = rawNote.trim();
      if (!text) return '';

      const parsed = this.parseMaybeJson(text);
      if (parsed !== null) {
        const parsedText = this.noteTextFromUnknown(parsed);
        if (parsedText) return parsedText;
      }

      return text;
    }

    return '';
  }

  private noteSortValue(note: any): number {
    const value = note?.date_last_modified || note?.date || note?.created || '';
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private firstNoteTextValue(row: DropTrailer, fields: string[]): string {
    for (const field of fields) {
      const value = this.noteTextFromUnknown((row as any)[field]);
      if (value) return value;
    }
    return '';
  }

  private firstObjectStringValue(source: Record<string, unknown>, fields: string[]): string {
    for (const field of fields) {
      const value = this.noteTextFromUnknown(source[field]);
      if (value) return value;
    }
    return '';
  }

  private parseMaybeJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private firstNumberValue(row: DropTrailer, fields: string[]): number | null {
    for (const field of fields) {
      const value = (row as any)[field];
      if (value === null || value === undefined || value === '') continue;
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) return numericValue;
    }
    return null;
  }

  private truncateText(value: string, maxLength: number): string {
    const text = String(value || '').trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
  }

  private readonly dateFormatter = (params: ValueFormatterParams): string => {
    if (!params.value) return '';

    const value = String(params.value);
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      return `${dateOnlyMatch[2]}/${dateOnlyMatch[3]}/${dateOnlyMatch[1]}`;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatLadingStatusDateForApi(value: any): string {
    const normalizedDate = this.normalizeDateInput(value);
    const match = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';

    return `${match[2]}/${match[3]}/${match[1]}`;
  }

  private defaultLadingStatusTimezone(): string | null {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  }

  private refreshInsights(): void {
    const fallbackLiveOps = this.buildFallbackLiveOps();
    const liveOps = this.mergeAnalyticsLiveOps(fallbackLiveOps);
    const volume = this.dropTrailerAnalytics?.volume ?? {};
    const topDc = this.dropTrailerAnalytics?.leaders?.top_distribution_centers?.[0];
    const scanPercent = liveOps.active_trailers
      ? Math.round((liveOps.scan_finished / liveOps.active_trailers) * 100)
      : 0;

    this.dropTrailerInsights = [
      {
        key: 'total',
        title: 'Active Trailers',
        value: liveOps.active_trailers,
        description: 'Current active trailers with YTD and MTD volume.',
        facts: [
          { label: 'YTD', value: this.formatCompactNumber(volume.ytd_cartons) },
          { label: 'MTD', value: this.formatCompactNumber(volume.mtd_cartons) },
          { label: 'Top DC', value: this.formatLeaderName(topDc?.name) },
        ],
        tone: 'sky',
        icon: 'fa-truck-ramp-box',
      },
      {
        key: 'dc',
        title: 'At DC',
        value: liveOps.at_dc,
        description: 'Active trailers still working through the distribution center.',
        facts: [
          { label: 'Waiting', value: this.formatInsightNumber(liveOps.waiting_load) },
          { label: 'Ready', value: this.formatInsightNumber(liveOps.ready_pickup) },
          { label: 'Missing #', value: this.formatInsightNumber(liveOps.missing_trailer_number) },
        ],
        tone: liveOps.at_dc ? 'amber' : 'emerald',
        icon: 'fa-warehouse',
      },
      {
        key: 'pool',
        title: 'Pool Work',
        value: liveOps.pool_work,
        description: 'Pool trailers still moving through offload.',
        facts: [
          { label: 'Offload', value: this.formatInsightNumber(liveOps.offloading) },
          { label: 'Ready Scan', value: this.formatInsightNumber(liveOps.ready_scan) },
          { label: 'Audit Open', value: this.formatCompactNumber(liveOps.active_received_not_audited_cartons) },
        ],
        tone: liveOps.pool_work ? 'rose' : 'emerald',
        icon: 'fa-boxes-stacked',
      },
      {
        key: 'scanned',
        title: 'Scan Finished',
        value: liveOps.scan_finished,
        description: 'Scan-complete trailers and downstream follow-up work.',
        facts: [
          { label: 'Rate', value: `${scanPercent}%` },
          { label: 'Notify', value: this.formatInsightNumber(liveOps.notify_due) },
          { label: 'Admin', value: this.formatInsightNumber(liveOps.admin_due) },
        ],
        tone: 'emerald',
        icon: 'fa-barcode',
      },
    ];
  }

  private buildFallbackLiveOps(): DropTrailerLiveOps {
    const activeTrailers = this.rowData.length;
    const atDc = this.rowData.filter((row) => this.isTrue(row.dropped_dc) && !this.isTrue(row.picked_up_dc)).length;
    const waitingLoad = this.rowData.filter((row) => this.isTrue(row.dropped_dc) && !this.isTrue(row.loaded_dc)).length;
    const readyPickup = this.rowData.filter((row) => this.isTrue(row.loaded_dc) && !this.isTrue(row.picked_up_dc)).length;
    const poolWork = this.rowData.filter((row) => this.isTrue(row.dropped_pool) && !this.isTrue(row.pool_scanning_finished)).length;
    const offloading = this.rowData.filter((row) => this.isTrue(row.pool_offload_started) && !this.isTrue(row.pool_offload_finished)).length;
    const readyScan = this.rowData.filter((row) => this.isTrue(row.pool_offload_finished) && !this.isTrue(row.pool_scanning_finished)).length;
    const scanFinished = this.rowData.filter((row) => this.isTrue(row.pool_scanning_finished)).length;
    const notifyDue = this.rowData.filter((row) =>
      this.isTrue(row.pool_scanning_finished) && !this.isTrue(row.notification_trailer_empty)
    ).length;
    const adminDue = this.rowData.filter((row) =>
      !String((row as any).pars_received || '').trim()
      || !String((row as any).pars_to_customs || '').trim()
      || !this.isTrue(row.appointment_plus_appointment_made)
      || !this.isTrue(row.appointment_plus_unload_time_set)
    ).length;
    const missingTrailerNumber = this.rowData.filter((row) => !String(row.carrier_trailer || '').trim()).length;

    return {
      active_trailers: activeTrailers,
      at_dc: atDc,
      pool_work: poolWork,
      scan_finished: scanFinished,
      waiting_load: waitingLoad,
      ready_pickup: readyPickup,
      offloading,
      ready_scan: readyScan,
      notify_due: notifyDue,
      admin_due: adminDue,
      missing_trailer_number: missingTrailerNumber,
      active_product_barcodes: 0,
      active_received_cartons: 0,
      active_audited_cartons: 0,
      active_received_not_audited_cartons: 0,
    };
  }

  private mergeAnalyticsLiveOps(fallback: DropTrailerLiveOps): DropTrailerLiveOps {
    const analyticsLiveOps = this.dropTrailerAnalytics?.live_ops ?? {};

    return {
      active_trailers: this.numberWithFallback(analyticsLiveOps.active_trailers, fallback.active_trailers),
      at_dc: this.numberWithFallback(analyticsLiveOps.at_dc, fallback.at_dc),
      pool_work: this.numberWithFallback(analyticsLiveOps.pool_work, fallback.pool_work),
      scan_finished: this.numberWithFallback(analyticsLiveOps.scan_finished, fallback.scan_finished),
      waiting_load: this.numberWithFallback(analyticsLiveOps.waiting_load, fallback.waiting_load),
      ready_pickup: this.numberWithFallback(analyticsLiveOps.ready_pickup, fallback.ready_pickup),
      offloading: this.numberWithFallback(analyticsLiveOps.offloading, fallback.offloading),
      ready_scan: this.numberWithFallback(analyticsLiveOps.ready_scan, fallback.ready_scan),
      notify_due: this.numberWithFallback(analyticsLiveOps.notify_due, fallback.notify_due),
      admin_due: this.numberWithFallback(analyticsLiveOps.admin_due, fallback.admin_due),
      missing_trailer_number: this.numberWithFallback(analyticsLiveOps.missing_trailer_number, fallback.missing_trailer_number),
      active_product_barcodes: this.numberWithFallback(analyticsLiveOps.active_product_barcodes, fallback.active_product_barcodes),
      active_received_cartons: this.numberWithFallback(analyticsLiveOps.active_received_cartons, fallback.active_received_cartons),
      active_audited_cartons: this.numberWithFallback(analyticsLiveOps.active_audited_cartons, fallback.active_audited_cartons),
      active_received_not_audited_cartons: this.numberWithFallback(
        analyticsLiveOps.active_received_not_audited_cartons,
        fallback.active_received_not_audited_cartons,
      ),
    };
  }

  private formatInsightNumber(value: number): string {
    return value.toLocaleString();
  }

  private formatCompactNumber(value: unknown): string {
    if (value === undefined || value === null || value === '') {
      return this.analyticsLoading ? '...' : '0';
    }

    const numericValue = Number(value ?? 0);
    if (!Number.isFinite(numericValue)) return '0';

    return new Intl.NumberFormat('en-US', {
      notation: Math.abs(numericValue) >= 10000 ? 'compact' : 'standard',
      maximumFractionDigits: 1,
    }).format(numericValue);
  }

  private formatLeaderName(value: unknown): string {
    const leaderName = String(value || '').trim();
    if (leaderName) return leaderName;
    return this.analyticsLoading ? '...' : 'None';
  }

  private numberWithFallback(value: unknown, fallback: number): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }

  private initializeTheme(): void {
    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (storedTheme === 'dark' || storedTheme === 'light') {
      this.themeMode = storedTheme;
      return;
    }

    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    this.themeMode = prefersDark ? 'dark' : 'light';
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  private isTrue(value: any): boolean {
    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase();
      return normalizedValue === 'true'
        || normalizedValue === 't'
        || normalizedValue === '1'
        || normalizedValue === 'yes'
        || normalizedValue === 'y';
    }

    return value === true || value === 1;
  }
}
